import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Modal, Drawer, Field } from "../components/UI";
import { Page } from "../components/AppShell";
import { GoogleLogo, MicrosoftOutlookLogo, EnvelopeSimple, Plug, X, PaperPlaneTilt, ArrowsClockwise, Plus, MagnifyingGlass, CheckSquare } from "@phosphor-icons/react";

export default function Email() {
  const [oauth, setOauth] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [inbox, setInbox] = useState({ items: [], errors: [], accounts_count: 0 });
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [openMsg, setOpenMsg] = useState(null);
  const [openCompose, setOpenCompose] = useState(false);
  const [openTask, setOpenTask] = useState(null); // {prefill}
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [banner, setBanner] = useState(null);

  const loadAccounts = () => api.get("/email/accounts").then(r => setAccounts(r.data));
  const loadInbox = async () => {
    setLoadingInbox(true);
    try {
      const params = search ? `?q=${encodeURIComponent(search)}` : "";
      const r = await api.get(`/email/inbox${params}`);
      setInbox(r.data);
    } catch (e) {
      setBanner({ type: "error", text: formatErr(e) });
    }
    setLoadingInbox(false);
  };

  useEffect(() => {
    api.get("/email/oauth/status").then(r => setOauth(r.data));
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    loadAccounts().then(() => loadInbox());
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "gmail") {
      setBanner({ type: "success", text: `Connected ${params.get("email") || "Gmail account"}.` });
      window.history.replaceState({}, "", "/email");
    } else if (params.get("error")) {
      setBanner({ type: "error", text: `OAuth error: ${params.get("error")}. Verify your Google redirect URI in Cloud Console.` });
      window.history.replaceState({}, "", "/email");
    }
    // Deep-link: open a specific email (used by the Tasks page "Linked email" badge)
    const oa = params.get("open_account");
    const om = params.get("open_msg");
    if (oa && om) {
      openMessage({ account_id: oa, id: om });
      window.history.replaceState({}, "", "/email");
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const connectGmail = async () => {
    try {
      const r = await api.get("/email/connect/gmail");
      window.location.href = r.data.url;
    } catch (e) { setBanner({ type: "error", text: formatErr(e) }); }
  };

  const connectOutlook = async () => {
    try { await api.get("/email/connect/outlook"); }
    catch (e) { setBanner({ type: "info", text: formatErr(e) }); }
  };

  const disconnect = async (id) => {
    if (!confirm("Disconnect this account?")) return;
    await api.delete(`/email/accounts/${id}`);
    await loadAccounts();
    await loadInbox();
  };

  const openMessage = async (item) => {
    try {
      const r = await api.get(`/email/message/${item.account_id}/${item.id}`);
      setOpenMsg({ ...r.data, account_id: item.account_id });
    } catch (e) { setBanner({ type: "error", text: formatErr(e) }); }
  };

  const hasGmail = accounts.some(a => a.provider === "gmail" && a.connected);

  return (
    <Page
      title="Unified Inbox"
      subtitle="COMMS / EMAIL ROUTING"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Email"]}
      testid="email-page"
      actions={
        <div className="flex gap-2">
          {hasGmail && <button className="btn btn-secondary" onClick={() => setOpenCompose(true)} data-testid="compose-email-btn"><Plus size={14} /> COMPOSE</button>}
          <button className="btn btn-ghost" onClick={loadInbox} disabled={loadingInbox} data-testid="refresh-inbox-btn"><ArrowsClockwise size={14} /></button>
        </div>
      }
    >

      {banner && (
        <div
          className="surface p-3 mb-4 border-l-4 flex items-start justify-between"
          style={{ borderLeftColor: banner.type === "error" ? "#EF4444" : banner.type === "success" ? "#10B981" : "#FACC15" }}
          data-testid="email-banner"
        >
          <div className="text-sm">{banner.text}</div>
          <button onClick={() => setBanner(null)} className="text-xs text-muted-foreground"><X size={14} /></button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <ProviderCard
          provider="gmail"
          name="Gmail / G Suite"
          logo={GoogleLogo}
          configured={oauth?.gmail_configured}
          accounts={accounts.filter(a => a.provider === "gmail")}
          onConnect={connectGmail}
          onDisconnect={disconnect}
          redirectUri={oauth?.gmail_redirect_uri}
        />
        <ProviderCard
          provider="outlook"
          name="Outlook / Microsoft 365"
          logo={MicrosoftOutlookLogo}
          configured={oauth?.outlook_configured}
          accounts={accounts.filter(a => a.provider === "outlook")}
          onConnect={connectOutlook}
          onDisconnect={disconnect}
          stub
        />
      </div>

      <div className="surface" data-testid="inbox-panel">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <EnvelopeSimple size={16} />
          <span className="heading">Inbox</span>
          <span className="label-xs">{inbox.accounts_count} ACCOUNT{inbox.accounts_count !== 1 ? "S" : ""}</span>
          <div className="ml-auto flex items-center gap-2 flex-1 max-w-md">
            <MagnifyingGlass size={14} className="text-muted-foreground" />
            <input
              className="input"
              placeholder="Search inbox (e.g. from:client@x.com)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadInbox(); }}
              data-testid="inbox-search-input"
            />
          </div>
        </div>

        {inbox.errors?.length > 0 && (
          <div className="p-3 text-xs text-destructive border-b border-border" data-testid="inbox-errors">
            {inbox.errors.map((e, i) => <div key={i}>{e.account}: {e.error}</div>)}
          </div>
        )}

        {!hasGmail ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="inbox-empty-state">
            <EnvelopeSimple size={32} weight="thin" className="mx-auto mb-3" />
            <div className="font-semibold">No connected accounts</div>
            <div className="text-sm">Connect Gmail above to populate this unified inbox.</div>
          </div>
        ) : loadingInbox ? (
          <div className="p-12 text-center text-muted-foreground">Loading…</div>
        ) : inbox.items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">Inbox is empty.</div>
        ) : (
          <table className="dense">
            <thead><tr><th>FROM</th><th>SUBJECT</th><th>SNIPPET</th><th>ACCOUNT</th><th className="text-right">DATE</th></tr></thead>
            <tbody>
              {inbox.items.map(m => (
                <tr key={`${m.account_id}-${m.id}`} className={`cursor-pointer ${m.unread ? "font-semibold" : ""}`} onClick={() => openMessage(m)} data-testid={`inbox-row-${m.id}`}>
                  <td className="max-w-xs truncate">{m.from}</td>
                  <td className="max-w-xs truncate">{m.subject}</td>
                  <td className="text-muted-foreground text-sm max-w-md truncate">{m.snippet}</td>
                  <td className="text-xs mono text-muted-foreground">{m.account_email}</td>
                  <td className="text-xs text-muted-foreground text-right whitespace-nowrap">{shortDate(m.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={!!openMsg} onClose={() => setOpenMsg(null)} title={openMsg?.subject || ""}
        footer={
          openMsg ? (
            <>
              <button className="btn btn-secondary" onClick={() => setOpenMsg(null)}>CLOSE</button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setOpenTask({
                    title: `Email: ${openMsg.subject}`,
                    description: `From: ${openMsg.from}\nDate: ${openMsg.date}\n\n${openMsg.text || stripHtml(openMsg.html) || openMsg.snippet}`,
                    email_account_id: openMsg.account_id,
                    email_message_id: openMsg.id,
                  });
                  setOpenMsg(null);
                }}
                data-testid="create-task-from-email-btn"
              >
                <CheckSquare size={14} /> CREATE TASK
              </button>
              <button className="btn btn-primary" onClick={() => { setOpenCompose({ to: openMsg.from, subject: `Re: ${openMsg.subject}`, account_id: openMsg.account_id, body: `\n\n---\n${openMsg.text || openMsg.snippet}` }); setOpenMsg(null); }} data-testid="reply-email-btn">
                <PaperPlaneTilt size={14} /> REPLY
              </button>
            </>
          ) : null
        }
      >
        {openMsg && (
          <div className="space-y-2 text-sm" data-testid="message-detail">
            <div><span className="label-xs mr-2">FROM</span> {openMsg.from}</div>
            <div><span className="label-xs mr-2">TO</span> {openMsg.to}</div>
            <div><span className="label-xs mr-2">DATE</span> <span className="mono text-xs">{openMsg.date}</span></div>
            <div className="border-t border-border pt-3 mt-3 max-h-96 overflow-y-auto whitespace-pre-wrap">
              {openMsg.text || stripHtml(openMsg.html) || openMsg.snippet}
            </div>
          </div>
        )}
      </Modal>

      <ComposeModal
        open={!!openCompose}
        prefill={typeof openCompose === "object" ? openCompose : null}
        accounts={accounts.filter(a => a.provider === "gmail" && a.connected)}
        onClose={() => setOpenCompose(false)}
        onSent={() => { setOpenCompose(false); setBanner({ type: "success", text: "Email sent." }); loadInbox(); }}
        onError={(t) => setBanner({ type: "error", text: t })}
      />

      <CreateTaskModal
        open={!!openTask}
        prefill={openTask}
        users={users}
        onClose={() => setOpenTask(null)}
        onCreated={() => { setOpenTask(null); setBanner({ type: "success", text: "Task created from email." }); }}
        onError={(t) => setBanner({ type: "error", text: t })}
      />
    </Page>
  );
}

function ProviderCard({ provider, name, logo: Logo, configured, accounts, onConnect, onDisconnect, redirectUri, stub }) {
  return (
    <div className="surface p-5" data-testid={`provider-card-${provider}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3"><Logo size={22} /><span className="heading">{name}</span></div>
        {stub
          ? <span className="badge badge-yellow">COMING SOON</span>
          : configured
            ? <span className="badge badge-green">READY</span>
            : <span className="badge badge-red">NO CREDENTIALS</span>}
      </div>
      {!configured && !stub && (
        <div className="text-xs text-muted-foreground mb-3">
          Add your <strong>Client ID + Secret</strong> in Settings → Email OAuth.
          {redirectUri && <div className="mt-1">Authorized redirect URI: <code className="kbd block mt-1 break-all">{redirectUri}</code></div>}
        </div>
      )}
      {accounts.length > 0 && (
        <div className="space-y-1 mb-3" data-testid={`${provider}-accounts-list`}>
          {accounts.map(a => (
            <div key={a.id} className="flex items-center justify-between text-sm bg-secondary/30 px-2 py-1">
              <span className="mono text-xs">{a.email}</span>
              <button onClick={() => onDisconnect(a.id)} className="text-xs text-destructive" data-testid={`disconnect-${a.id}`}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <button className="btn btn-secondary w-full justify-center" disabled={stub || !configured} onClick={onConnect} data-testid={`connect-${provider}-btn`}>
        <Plug size={14} /> {stub ? "OUTLOOK COMING SOON" : configured ? `CONNECT ${provider.toUpperCase()}` : "AWAITING CREDENTIALS"}
      </button>
    </div>
  );
}

function ComposeModal({ open, prefill, accounts, onClose, onSent, onError }) {
  const [form, setForm] = useState({ account_id: "", to: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({
        account_id: prefill?.account_id || (accounts[0]?.id || ""),
        to: prefill?.to || "",
        subject: prefill?.subject || "",
        body: prefill?.body || "",
      });
    }
  }, [open, prefill, accounts]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/email/send", form);
      onSent();
    } catch (e) { onError(formatErr(e)); }
    setBusy(false);
  };

  return (
    <Drawer open={open} onClose={onClose} title="Compose email" size="medium" testid="compose-email-drawer"
      footer={<><button className="btn btn-secondary" onClick={onClose}>CANCEL</button><button className="btn btn-primary" onClick={send} disabled={busy || !form.to || !form.subject} data-testid="send-email-btn"><PaperPlaneTilt size={14} /> SEND</button></>}>
      <Field label="FROM">
        <select className="input" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} data-testid="compose-from-select">
          {accounts.map(a => <option key={a.id} value={a.id}>{a.email}</option>)}
        </select>
      </Field>
      <Field label="TO"><input className="input" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} placeholder="someone@example.com" data-testid="compose-to-input" /></Field>
      <Field label="SUBJECT"><input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="compose-subject-input" /></Field>
      <Field label="MESSAGE"><textarea className="input" rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="compose-body-input" /></Field>
    </Drawer>
  );
}

function CreateTaskModal({ open, prefill, users, onClose, onCreated, onError }) {
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", assignee_id: "", due_date: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && prefill) {
      setForm({
        title: prefill.title || "",
        description: prefill.description || "",
        priority: "medium",
        assignee_id: "",
        due_date: "",
      });
    }
  }, [open, prefill]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/tasks", {
        title: form.title,
        description: form.description,
        priority: form.priority,
        status: "todo",
        assignee_id: form.assignee_id || null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        email_account_id: prefill?.email_account_id || null,
        email_message_id: prefill?.email_message_id || null,
      });
      onCreated();
    } catch (e) { onError(formatErr(e)); }
    setBusy(false);
  };

  return (
    <Drawer open={open} onClose={onClose} title="Create task from email" size="narrow" testid="email-create-task-drawer"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>CANCEL</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !form.title} data-testid="save-task-from-email-btn">
            <CheckSquare size={14} /> CREATE TASK
          </button>
        </>
      }>
      <Field label="TITLE">
        <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="PRIORITY">
          <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-priority-select">
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
          </select>
        </Field>
        <Field label="ASSIGNEE">
          <select className="input" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} data-testid="task-assignee-select">
            <option value="">unassigned</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="DUE DATE">
        <input className="input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" />
      </Field>
      <Field label="DESCRIPTION (from email)">
        <textarea className="input" rows={8} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description-input" />
      </Field>
    </Drawer>
  );
}

function shortDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
}

function stripHtml(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}
